import React, { useState, useEffect } from 'react';
import '../styles/components/CachePopup.css';
import { cookieConsentTranslations } from '../translations/cookieConsent';

const CachePopup = ({ currentLanguage = 'fr' }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState({
    analytics: false,
    advertising: false,
    hotjar: false
  });
  const t = cookieConsentTranslations[currentLanguage] || cookieConsentTranslations.fr;

  useEffect(() => {
    const saved = localStorage.getItem('cacheConsent');
    if (!saved) {
      const timer = setTimeout(() => setIsVisible(true), 1200);
      return () => clearTimeout(timer);
    }
  }, []);

  const saveAndClose = (prefs) => {
    localStorage.setItem('cacheConsent', JSON.stringify(prefs));
    setIsVisible(false);
  };

  const handleAcceptAll = () => {
    saveAndClose({ analytics: true, advertising: true, hotjar: true });
  };

  const handleRejectAll = () => {
    saveAndClose({ analytics: false, advertising: false, hotjar: false });
  };

  const handleSavePreferences = () => {
    saveAndClose(preferences);
  };

  const toggleSelectAll = () => {
    const allOn = Object.values(preferences).every(Boolean);
    const val = !allOn;
    setPreferences({ analytics: val, advertising: val, hotjar: val });
  };

  const togglePreference = (key) => {
    setPreferences(prev => ({ ...prev, [key]: !prev[key] }));
  };

  if (!isVisible) return null;

  return (
    <div className="cache-overlay">
      <div className="cache-popup">
        <div className="cache-popup-inner">
          {!showPreferences ? (
            /* ── Main view ── */
            <>
              <div className="cache-popup-header">
                <span className="cookie-icon">🍪</span>
                <h3>{t.title}</h3>
              </div>
              <p className="cache-popup-desc">{t.description}</p>

              <div className="cache-essential-badge">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="badge-icon">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                </svg>
                <div>
                  <strong>{t.essentialLabel}</strong>
                  <span>{t.essentialDescription}</span>
                </div>
              </div>

              <div className="cache-popup-actions">
                <button className="btn-accept" onClick={handleAcceptAll}>
                  {t.acceptAll}
                </button>
                <button className="btn-reject" onClick={handleRejectAll}>
                  {t.rejectAll}
                </button>
                <button className="btn-customize" onClick={() => setShowPreferences(true)}>
                  {t.customize}
                </button>
              </div>

              <a href="/privacy" className="cache-privacy-link" target="_blank" rel="noopener noreferrer">
                {t.privacyLink}
              </a>
            </>
          ) : (
            /* ── Preferences view ── */
            <>
              <div className="cache-popup-header">
                <button className="btn-back" onClick={() => setShowPreferences(false)} aria-label="Back">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="15 18 9 12 15 6" />
                  </svg>
                </button>
                <h3>{t.customize}</h3>
              </div>

              <div className="cookie-preferences">
                {/* Essential — always on */}
                <div className="pref-item pref-essential">
                  <div className="pref-info">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="pref-icon">
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                    </svg>
                    <div className="pref-text">
                      <span className="pref-name">{t.essentialLabel}</span>
                      <span className="pref-desc">{t.essentialDescription}</span>
                    </div>
                  </div>
                  <label className="toggle-switch toggle-disabled">
                    <input type="checkbox" checked disabled />
                    <span className="slider"></span>
                  </label>
                </div>

                {/* Select all */}
                <div className="pref-item pref-select-all">
                  <div className="pref-info">
                    <span className="pref-name">{t.selectAll}</span>
                  </div>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={Object.values(preferences).every(Boolean)}
                      onChange={toggleSelectAll}
                    />
                    <span className="slider"></span>
                  </label>
                </div>

                {/* Individual services */}
                {Object.entries(t.services).map(([key, service]) => (
                  <div key={key} className="pref-item">
                    <div className="pref-info">
                      <span className="pref-service-icon">{service.icon}</span>
                      <div className="pref-text">
                        <span className="pref-name">{service.name}</span>
                        <span className="pref-desc">{service.description}</span>
                      </div>
                    </div>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={preferences[key]}
                        onChange={() => togglePreference(key)}
                      />
                      <span className="slider"></span>
                    </label>
                  </div>
                ))}
              </div>

              <div className="cache-popup-actions">
                <button className="btn-accept" onClick={handleSavePreferences}>
                  {t.savePreferences}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CachePopup;
